import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Edit } from '@refinedev/antd';
import { useOne, useList } from '@refinedev/core';
import {
  Form,
  Input,
  Select,
  Switch,
  Checkbox,
  Button,
  message,
  Alert,
  Descriptions,
  Tag,
} from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { authClient } from '../../providers/axios';
import {
  getRolePrefix,
  buildRoleName,
  parseRoleName,
  validateCustomerRoleName,
} from './utils/roleNameUtils';
import type { RoleScope } from './utils/roleNameUtils';
import { useRoleAccess, canSaveInCompanyLimit } from './utils/roleAccess';
import { IdentityErrorAlert } from './components/IdentityErrorAlert';
import { PermissionPicker } from './components/PermissionPicker';
import { canEditRole } from './utils/systemRoles';
import { SystemRoleEditForm } from './SystemRoleEditForm';

interface Role {
  id: string;
  _id?: string;
  name: string;
  description?: string;
  scope?: 'internal' | 'customer';
  companyId?: string | null;
  companyName?: string | null;
  permissions: string[];
  isSystem?: boolean;
  seedPermissions?: string[];
}

interface Permission {
  key: string;
  description?: string;
  category?: string;
  scope?: 'internal' | 'customer';
}

export const RolesEditPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [scope, setScope] = useState<RoleScope>('internal');
  const [isShared, setIsShared] = useState(false);
  const [companyId, setCompanyId] = useState<string>('');
  const access = useRoleAccess();
  // A customer role manager only ever opens customer roles and can't move one to internal.
  const { customerOnly, limitedCompanies } = access;

  const { data: roleData, isLoading: roleLoading } = useOne<Role>({
    resource: 'roles',
    id: id!,
    meta: { dataProviderName: 'authDataProvider' },
  });

  const {
    data: permissionsData,
    isLoading: permissionsLoading,
    isError: permissionsError,
  } = useList<Permission>({
    resource: 'permissions',
    meta: { dataProviderName: 'authDataProvider' },
    pagination: { mode: 'off' },
  });

  const role = roleData?.data;
  const allPermissions = permissionsData?.data ?? [];

  // Filter permissions by scope, and to the ones this person may grant
  const filteredPermissions = allPermissions.filter((p) => {
    if (!access.canGrant(p.key)) return false;
    if (scope === 'customer') return p.scope === 'customer';
    return true;
  });

  const prefix = getRolePrefix(scope, companyId || null, isShared);
  // Only treat as legacy when scope hasn't changed — if the user changes scope the role
  // will get a new prefix on save, so the legacy-name protection no longer applies.
  const isLegacyUnprefixedRole = Boolean(
    role &&
    scope === (role.scope ?? 'internal') &&
    ((role.scope === 'internal' && !role.name.startsWith('sf_')) ||
      (role.scope === 'customer' && !role.companyId && !role.name.startsWith('default_'))),
  );

  // Populate form once role data arrives
  useEffect(() => {
    if (role) {
      const s = (role.scope ?? 'internal') as RoleScope;
      setScope(s);
      const parsed = parseRoleName(role.name, role.companyId);
      const shared = s === 'customer' && !role.companyId;
      setIsShared(shared);
      setCompanyId(role.companyId ?? '');
      form.setFieldsValue({
        name: parsed.userPart,
        description: role.description ?? '',
        scope: s,
        companyId: role.companyId ?? '',
        permissions: role.permissions ?? [],
      });
    }
  }, [role, form]);

  const handleSubmit = async (values: {
    name: string;
    description?: string;
    scope: string;
    companyId?: string;
    permissions: string[];
  }) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const preservingLegacyName =
        isLegacyUnprefixedRole &&
        values.scope === (role?.scope ?? 'internal') &&
        (values.scope !== 'customer' ||
          (!isShared && (values.companyId || null) === (role?.companyId || null)) ||
          (isShared && !(role?.companyId || null)));

      const fullName = preservingLegacyName
        ? values.name
        : buildRoleName(values.name, values.scope as RoleScope, values.companyId || null, isShared);
      const payload = {
        ...values,
        name: fullName,
        companyId: values.scope === 'customer' && !isShared ? values.companyId || null : null,
      };
      await authClient.patch(`/roles/${id}`, payload);
      message.success('Role updated');
      navigate('/roles');
    } catch {
      message.error('Failed to update role');
    } finally {
      setSubmitting(false);
    }
  };

  if (roleLoading || !access.loaded) return <div>Loading...</div>;

  if (role && access.identityError) {
    return (
      <Edit
        title={role.name}
        saveButtonProps={{ style: { display: 'none' } }}
        canDelete={false}
        footerButtons={null}
      >
        <IdentityErrorAlert access={access} />
      </Edit>
    );
  }

  // A customer role manager can't save a role for a company outside their list, or one holding a
  // permission they don't have themselves (the auth service refuses both), so show it read only.
  const cantSaveReason =
    role && customerOnly
      ? !canSaveInCompanyLimit(role, limitedCompanies)
        ? 'You can only change roles for your own companies.'
        : (role.permissions ?? []).some((p) => !access.canGrant(p))
          ? "This role has permissions you don't hold yourself, so you can't change it."
          : null
      : null;
  if (role && cantSaveReason) {
    return (
      <Edit
        title={role.name}
        saveButtonProps={{ style: { display: 'none' } }}
        canDelete={false}
        footerButtons={null}
      >
        <Alert
          type="info"
          showIcon
          message="You can't change this role"
          description={cantSaveReason}
        />
      </Edit>
    );
  }

  // Built-in roles: permissions set in code are locked, others can be added.
  if (role?.isSystem && canEditRole(role)) {
    return (
      <Edit
        title={
          <span>
            <LockOutlined style={{ marginRight: 6 }} />
            {role.name}
          </span>
        }
        saveButtonProps={{ style: { display: 'none' } }}
        // Built-in roles can't be deleted (the auth service refuses it too).
        canDelete={false}
      >
        <SystemRoleEditForm
          key={role.id || role._id || id}
          role={{ ...role, id: role.id || role._id || id! }}
          permissions={allPermissions}
          permissionsLoading={permissionsLoading}
          permissionsError={permissionsError}
        />
      </Edit>
    );
  }

  // Super admin: read-only, it already holds every permission.
  if (role?.isSystem) {
    const s = role.scope ?? 'internal';
    return (
      <Edit
        title={
          <span>
            <LockOutlined style={{ marginRight: 6 }} />
            {role.name}
          </span>
        }
        saveButtonProps={{ style: { display: 'none' } }}
        footerButtons={null}
      >
        <Alert
          type="warning"
          showIcon
          message="This role can't be edited"
          description="The super admin role already has every permission, so there is nothing to add."
          style={{ marginBottom: 16 }}
        />
        <Descriptions bordered column={1}>
          <Descriptions.Item label="Name">{role.name}</Descriptions.Item>
          <Descriptions.Item label="Description">{role.description || '—'}</Descriptions.Item>
          <Descriptions.Item label="Scope">
            <Tag color={s === 'customer' ? 'orange' : 'purple'}>{s}</Tag>
          </Descriptions.Item>
          {s === 'customer' && (
            <>
              <Descriptions.Item label="Company ID">{role.companyId || '—'}</Descriptions.Item>
              <Descriptions.Item label="Company Name">{role.companyName || '—'}</Descriptions.Item>
            </>
          )}
          <Descriptions.Item label="Permissions">
            {role.permissions?.length
              ? role.permissions.map((p) => (
                  <Tag key={p} color="purple" style={{ marginBottom: 4 }}>
                    {p}
                  </Tag>
                ))
              : '—'}
          </Descriptions.Item>
        </Descriptions>
      </Edit>
    );
  }

  return (
    <Edit
      title="Edit Role"
      saveButtonProps={{ style: { display: 'none' } }}
      canDelete={access.canDelete}
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item
          label="Scope"
          name="scope"
          rules={[{ required: true, message: 'Scope is required' }]}
        >
          <Select
            disabled={customerOnly}
            options={[
              ...(customerOnly ? [] : [{ label: 'Internal', value: 'internal' }]),
              { label: 'Customer', value: 'customer' },
            ]}
            onChange={(val) => {
              setScope(val);
              setIsShared(false);
              setCompanyId('');
              form.setFieldsValue({ companyId: '', permissions: [] });
            }}
          />
        </Form.Item>

        {/* A manager limited to some companies can't make roles shared by every customer. */}
        {scope === 'customer' && !limitedCompanies && (
          <Form.Item label="Shared Role (applies to all customers)">
            <Switch
              checked={isShared}
              onChange={(checked) => {
                setIsShared(checked);
                if (checked) {
                  setCompanyId('');
                  form.setFieldsValue({ companyId: '' });
                }
              }}
            />
          </Form.Item>
        )}

        {scope === 'customer' && !isShared && (
          <Form.Item
            label="Company ID"
            name="companyId"
            rules={[
              { required: true, message: 'Company ID is required for customer-scoped roles' },
            ]}
          >
            {limitedCompanies ? (
              <Select
                placeholder="Company ID"
                options={limitedCompanies.map((c) => ({ label: c, value: c }))}
                onChange={(val: string) => setCompanyId(val)}
              />
            ) : (
              <Input placeholder="Company ID" onChange={(e) => setCompanyId(e.target.value)} />
            )}
          </Form.Item>
        )}

        <Form.Item
          label="Name"
          name="name"
          rules={[
            { required: true, message: 'Name is required' },
            {
              validator: (_, value) => {
                if (scope === 'customer' && !isShared && value) {
                  const err = validateCustomerRoleName(value);
                  if (err) return Promise.reject(err);
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <Input
            addonBefore={isLegacyUnprefixedRole ? undefined : prefix || undefined}
            placeholder={
              isLegacyUnprefixedRole
                ? 'Role name'
                : prefix
                  ? 'Role name suffix'
                  : 'Role name (must not start with sf_ or default_)'
            }
          />
        </Form.Item>

        <Form.Item label="Description" name="description">
          <Input.TextArea rows={3} placeholder="Optional description" />
        </Form.Item>

        <Form.Item label="Permissions" name="permissions">
          {permissionsLoading ? (
            <div>Loading permissions...</div>
          ) : permissionsError ? (
            <Alert type="error" showIcon message="Couldn't load the permissions list" />
          ) : (
            <Checkbox.Group style={{ width: '100%' }}>
              <PermissionPicker permissions={filteredPermissions} />
            </Checkbox.Group>
          )}
        </Form.Item>

        <Form.Item>
          <Button type="primary" htmlType="submit" loading={submitting}>
            Save Changes
          </Button>
        </Form.Item>
      </Form>
    </Edit>
  );
};
